<template lang="pug">
phone-input(
  v-if="field.inputType === 'tel'",
  :modelValue="getFieldString(field)",
  @update:modelValue="updateFieldValue(field, $event)",
  :id="fieldControlId",
  :aria-describedby="ariaDescribedby",
  :aria-invalid="ariaInvalid",
  :aria-required="ariaRequired",
  :disabled="disabled || (field.nullable && flatUser[field.property] === null)"
)
el-input(
  v-else-if="field.type === 'text'",
  :modelValue="getFieldValue(field)",
  @update:modelValue="updateFieldValue(field, $event)",
  :id="fieldControlId",
  :type="field.inputType ? field.inputType : 'text'",
  :placeholder="$t(field.label)",
  :aria-describedby="ariaDescribedby",
  :aria-invalid="ariaInvalid",
  :aria-required="ariaRequired",
  :disabled="disabled || (field.nullable && flatUser[field.property] === null)"
)

el-date-picker.fill-width(
  v-else-if="field.type === 'date'",
  :modelValue="getFieldValue(field)",
  @update:modelValue="updateFieldValue(field, $event)",
  :id="fieldControlId",
  type="date",
  :format="dateFormat",
  value-format="YYYY-MM-DD",
  :placeholder="$t(field.label)",
  :aria-describedby="ariaDescribedby",
  :aria-invalid="ariaInvalid",
  :aria-required="ariaRequired",
  :disabled="disabled"
)
el-date-picker.fill-width(
  v-else-if="field.type === 'year'",
  :modelValue="getFieldValue(field)",
  @update:modelValue="updateFieldValue(field, $event)",
  :id="fieldControlId",
  type="year",
  value-format="YYYY",
  :placeholder="$t(field.label)",
  :aria-describedby="ariaDescribedby",
  :aria-invalid="ariaInvalid",
  :aria-required="ariaRequired",
  :disabled="disabled"
)

template(v-else-if="field.type === 'address' && userEditing.attributes")
  el-input(
    :modelValue="userEditing.attributes.address || ''",
    @update:modelValue="updateAttribute('address', $event)",
    :id="fieldControlId",
    type="text",
    :placeholder="$t('address')",
    :aria-describedby="ariaDescribedby",
    :aria-invalid="ariaInvalid",
    :aria-required="ariaRequired",
    :disabled="disabled"
  )
  .flex-row.mt-1.flex-center(v-if="showAddressDetails")
    zipcode-input.zipcode.mr-1(
      :modelValue="getAttributeString('zipCode')",
      @update:modelValue="updateAttribute('zipCode', $event)",
      :id="addressZipCodeId",
      :aria-describedby="ariaDescribedby",
      :aria-invalid="ariaInvalid",
      :aria-required="ariaRequired",
      :disabled="disabled"
    )
    input.width-zero(
      :value="userEditing.attributes.city || ''",
      @input="updateAttributeFromEvent('city', $event)",
      :id="addressCityId",
      type="text",
      :placeholder="$t('city')",
      :aria-describedby="ariaDescribedby",
      :aria-invalid="ariaInvalid",
      :aria-required="ariaRequired",
      :disabled="disabled"
    )
  country-picker.address-country-picker(
    v-if="showAddressDetails",
    :modelValue="getAttributeString('country')",
    @update:modelValue="updateAttribute('country', $event)",
    :id="addressCountryId",
    :aria-describedby="ariaDescribedby",
    :aria-invalid="ariaInvalid",
    :aria-required="ariaRequired",
    :disabled="disabled"
  )
el-input(
  v-else,
  :modelValue="getFieldValue(field)",
  @update:modelValue="updateFieldValue(field, $event)",
  type="text",
  :placeholder="$t(field.label)",
  :id="fieldControlId",
  :aria-describedby="ariaDescribedby",
  :aria-invalid="ariaInvalid",
  :aria-required="ariaRequired",
  :disabled="disabled"
)
el-checkbox(
  v-if="field.nullable && field.nullLabel !== undefined",
  :id="nullableCheckboxId",
  :label="$t(field.nullLabel)",
  @update:modelValue="(val: boolean) => updateFieldValue(field, val ? null : undefined)",
  :modelValue="flatUser[field.property] === null",
  :aria-describedby="ariaDescribedby",
  :aria-invalid="ariaInvalid",
  :aria-required="ariaRequired",
  :disabled="disabled"
)
</template>

<script lang="ts" setup>
import { type AccountsTypes } from "@inctec/elearning-ui-client";
import { computed } from "vue";
import CountryPicker from "@/components/elements/CountryPicker/CountryPicker.vue";
import PhoneInput from "@/components/elements/PhoneInput.vue";
import ZipcodeInput from "@/components/elements/ZipcodeInput.vue";
import { type RegistrationField } from "@/components/user/userData";

const props = defineProps<{
  user: Partial<AccountsTypes.User>;
  field: RegistrationField;
  controlId?: string;
  id?: string;
  ariaDescribedby?: string;
  ariaInvalid?: boolean;
  ariaRequired?: boolean;
  disabled?: boolean;
  showAddressDetails?: boolean;
  dateFormat?: string;
}>();

const showAddressDetails = computed(() => props.showAddressDetails ?? true);
const dateFormat = computed(() => props.dateFormat ?? "DD.MM.YYYY");
const fieldControlId = computed(() => props.controlId ?? props.id);

const emit = defineEmits<{
  (e: "update:user", value: Partial<AccountsTypes.User>): void;
}>();

const userEditing = computed({
  get: () => props.user,
  set: (val: Partial<AccountsTypes.User>) => emit("update:user", val),
});

const addressZipCodeId = computed(() =>
  fieldControlId.value ? `${fieldControlId.value}-zip-code` : undefined,
);
const addressCityId = computed(() =>
  fieldControlId.value ? `${fieldControlId.value}-city` : undefined,
);
const addressCountryId = computed(() =>
  fieldControlId.value ? `${fieldControlId.value}-country` : undefined,
);
const nullableCheckboxId = computed(() =>
  fieldControlId.value ? `${fieldControlId.value}-null` : undefined,
);

// NOTE: for reactivity, a flattened user object is needed, to access the fields dynamically
const flatUser = computed<Record<string, unknown>>(() => {
  return {
    ...userEditing.value,
    ...userEditing.value.attributes,
  };
});

// Helper functions to get and set field values based on whether they're in attributes or root
function getFieldValue(field: {
  attributes?: boolean;
  property: string;
}): unknown {
  if (field.attributes) {
    return userEditing.value?.attributes?.[field.property];
  }
  return userEditing.value[field.property as keyof Partial<AccountsTypes.User>];
}

function getFieldString(field: {
  attributes?: boolean;
  property: string;
}): string {
  const value = getFieldValue(field);
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

function updateFieldValue(
  field: RegistrationField,
  value: string | number | undefined | null,
): void {
  if (field.attributes) {
    userEditing.value = {
      ...userEditing.value,
      attributes: {
        ...userEditing.value.attributes,
        [field.property]: value,
      },
    };
  } else {
    userEditing.value = {
      ...userEditing.value,
      [field.property]: value,
    };
  }
}

function updateAttribute(property: string, value: string | undefined): void {
  userEditing.value = {
    ...userEditing.value,
    attributes: {
      ...userEditing.value.attributes,
      [property]: value,
    },
  };
}

function updateAttributeFromEvent(property: string, event: Event): void {
  if (!(event.target instanceof HTMLInputElement)) return;
  updateAttribute(property, event.target.value);
}

function getAttributeString(property: string): string {
  const value = userEditing.value.attributes?.[property];
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}
</script>

<style scoped>
.country-picker {
  margin-top: 1rem;
}
</style>
